import { League, LeagueStanding, Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { FlatList, StyleSheet, Text, View } from 'react-native';

interface LeaderboardTabProps {
  standings: LeagueStanding[];
  league: League;
  tournaments: Tournament[];
  colors: any;
}

export const LeaderboardTab = ({ standings, league, tournaments, colors }: LeaderboardTabProps) => {
  const { t } = useTranslation();

  const completedCount = tournaments.filter((t) => t.status === 'completed').length;

  const getMedal = (position: number) => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${position}`;
    }
  };

  if (standings.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="podium" size={64} color={colors.textTertiary} />
        <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
          {t('leagues.noStandingsYet')}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.headerInfo, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.headerTitle, { color: colors.text }]}>{t('leagues.leaderboard')}</Text>
        <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
          {completedCount}/{tournaments.length} {t('leagues.tournamentsCompleted')}
        </Text>
        <Text style={[styles.pointSystemText, { color: colors.textTertiary }]}>
          {t('leagues.pointSystem')}: {Array.isArray(league.pointSystem) ? league.pointSystem.join(', ') : ''}
        </Text>
      </View>

      <FlatList
        data={standings}
        keyExtractor={(item) => item.participantName}
        renderItem={({ item }) => (
          <View style={[styles.standingRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.positionContainer}>
              <Text style={[styles.position, { color: item.position <= 3 ? colors.primary : colors.text }]}>
                {getMedal(item.position)}
              </Text>
            </View>
            <View style={styles.participantInfo}>
              <View style={styles.nameRow}>
                <Text style={[styles.participantName, { color: colors.text }]}>{item.participantName}</Text>
                <Text style={[styles.tournamentsPlayed, { color: colors.textTertiary }]}>
                  {item.tournamentsPlayed}/{completedCount} {t('leagues.tournamentsPlayed')}
                </Text>
              </View>
              <View style={styles.resultsRow}>
                {item.tournamentResults.map((result) => (
                  <View
                    key={result.tournamentId}
                    style={[styles.resultBadge, { backgroundColor: colors.surface, borderColor: colors.border }]}
                  >
                    <Text style={[styles.resultText, { color: colors.textSecondary }]} numberOfLines={1}>
                      #{result.position} ({result.pointsAwarded}pts)
                    </Text>
                  </View>
                ))}
              </View>
            </View>
            <View style={styles.pointsContainer}>
              <Text style={[styles.totalPoints, { color: colors.primary }]}>{item.totalPoints}</Text>
              <Text style={[styles.pointsLabel, { color: colors.textTertiary }]}>pts</Text>
            </View>
          </View>
        )}
        contentContainerStyle={styles.listContent}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  headerInfo: {
    padding: 15,
    borderBottomWidth: 1,
  },
  headerTitle: { fontSize: 18, fontWeight: 'bold' },
  headerSubtitle: { fontSize: 14, marginTop: 4 },
  pointSystemText: { fontSize: 12, marginTop: 4 },
  listContent: { padding: 15, gap: 8 },
  standingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
  },
  positionContainer: { width: 40, alignItems: 'center' },
  position: { fontSize: 18, fontWeight: 'bold' },
  participantInfo: { flex: 1, marginLeft: 8 },
  nameRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  participantName: { fontSize: 16, fontWeight: '600' },
  tournamentsPlayed: { fontSize: 11 },
  resultsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  resultBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, borderWidth: 1 },
  resultText: { fontSize: 11 },
  pointsContainer: { alignItems: 'center', marginLeft: 8 },
  totalPoints: { fontSize: 20, fontWeight: 'bold' },
  pointsLabel: { fontSize: 11 },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
  emptyText: { fontSize: 16, marginTop: 16, textAlign: 'center' },
});
